import type { Decor, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shadow, shade, sun, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';

/**
 * Hualien extras: Qingshui Cliffs, the Tunnel of Nine Turns, two snack stalls, the Amis harvest
 * dance, whale watching at the harbour, the Taroko Gate, the welcome board, the pier, and the
 * seaside hotel's reception and bed.
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

// ---------------------------------------------------------------------------
// Small helpers (same look as props4)
// ---------------------------------------------------------------------------

export function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

export function sign(c: Ctx, text: string, cx: number, y: number, w: number, bg: string, fg = '#fff6dc'): void {
  block(c, cx - w / 2, y, w, 11, bg, shade(bg, 0.25), shade(bg, -0.3));
  txt(c, text, cx, y + 5.9, 8, fg, 900, 'rgba(0,0,0,0.35)');
}

export function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

export function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

export function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

export const isNight = () => sun.alpha < 0.8;

/** A night-market stall (same look as props3/props4): counter, striped canopy, glowing sign, bulb. */
export function stall(c: Ctx, r: Rect, t: number, label: string, canopy: string, goods: (c: Ctx, r: Rect, t: number) => void): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3.5, 0.35);
  rect(c, OUTLINE, x + 1, y - 34, 2, 36);
  rect(c, OUTLINE, x + w - 3, y - 34, 2, 36);
  const top = y - 40;
  for (let i = 0; i < w; i += 6) {
    c.fillStyle = (i / 6) % 2 ? '#fff6e6' : canopy;
    c.fillRect(x - 2 + i, top, Math.min(6, w + 4 - i), 9);
    c.beginPath();
    c.arc(x + 1 + i, top + 9, 3, 0, Math.PI);
    c.fill();
  }
  rect(c, 'rgba(0,0,0,0.25)', x - 2, top + 9, w + 4, 1);
  sign(c, label, x + w / 2, top - 11, Math.max(28, label.length * 9 + 8), '#c0392b');
  const sw = wind.at(x, y) * 1.2 + Math.sin(t * 1.7 + x) * 0.6;
  line(c, OUTLINE, 0.6, [x + w / 2, top + 9, x + w / 2 + sw, top + 16]);
  ellipse(c, '#fff3b0', x + w / 2 + sw, top + 17.5, 1.8, 2);
  ellipse(c, 'rgba(255,230,140,0.25)', x + w / 2 + sw, top + 18, 6, 5);
  goods(c, r, t);
  block(c, x, y - 8, w, h + 8, '#9a6a44', '#b88a5c', '#6e4a2e');
  rect(c, '#f2e6cf', x + 1, y - 9, w - 2, 2);
  for (let i = 4; i < w - 2; i += 8) rect(c, 'rgba(0,0,0,0.15)', x + i, y - 6, 0.6, h + 4);
}

export function steam(c: Ctx, x: number, y: number, t: number, n = 3, a = 0.35): void {
  for (let i = 0; i < n; i++) {
    const k = (t * 0.6 + i / n) % 1;
    ellipse(c, `rgba(255,255,255,${a * (1 - k)})`, x + Math.sin(t * 2 + i * 2) * 2, y - k * 12, 1.5 + k * 2, 1.2 + k * 1.5);
  }
}

/** A little side-on whale (icon style). */
export function whale(c: Ctx, cx: number, cy: number, s: number, col: string, belly: string): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(cx - 7 * s, cy);
  c.quadraticCurveTo(cx - 6 * s, cy - 4.4 * s, cx + 1 * s, cy - 4 * s);
  c.quadraticCurveTo(cx + 5 * s, cy - 3.6 * s, cx + 6 * s, cy - 1.6 * s);
  c.lineTo(cx + 9 * s, cy - 4 * s);
  c.lineTo(cx + 8.4 * s, cy - 0.6 * s);
  c.lineTo(cx + 9.6 * s, cy + 1.4 * s);
  c.lineTo(cx + 5.6 * s, cy + 0.6 * s);
  c.quadraticCurveTo(cx + 1 * s, cy + 3 * s, cx - 4 * s, cy + 2 * s);
  c.quadraticCurveTo(cx - 7 * s, cy + 1.6 * s, cx - 7 * s, cy);
  c.fill();
  c.fillStyle = belly;
  c.beginPath();
  c.moveTo(cx - 6.4 * s, cy + 0.6 * s);
  c.quadraticCurveTo(cx - 1 * s, cy + 1.4 * s, cx + 4 * s, cy + 0.6 * s);
  c.quadraticCurveTo(cx, cy + 2.6 * s, cx - 4 * s, cy + 2 * s);
  c.closePath();
  c.fill();
  ellipse(c, '#1b2a36', cx - 4 * s, cy - 1 * s, 0.6 * s, 0.6 * s);
  // Spout.
  line(c, '#bfe6ff', 0.6 * s, [cx - 3 * s, cy - 4.4 * s, cx - 3 * s, cy - 6.4 * s]);
  ellipse(c, '#bfe6ff', cx - 4.2 * s, cy - 7 * s, 1.2 * s, 0.7 * s);
  ellipse(c, '#bfe6ff', cx - 1.8 * s, cy - 7 * s, 1.2 * s, 0.7 * s);
}

/** Simple mountains-meet-the-sea picture (used on the welcome board and the cliff panel). */
export function vista(c: Ctx, x: number, y: number, w: number, h: number, cliff: boolean): void {
  vgrad(c, x, y, w, h, '#9fd6f0', '#e8f6fb');
  const sea = y + h * 0.62;
  vgrad(c, x, sea, w, h - (sea - y), '#2f9fb8', '#1f6f9a');
  for (let i = 0; i < 3; i++) rect(c, 'rgba(255,255,255,0.55)', x + 2 + i * (w / 3), sea + 2 + (i % 2), w / 6, 0.5);
  if (cliff) {
    // Grey-green marble walls dropping straight into the water on the left.
    poly(c, '#5a8a5a', [x, sea + 4, x, y + h * 0.15, x + w * 0.18, y + h * 0.08, x + w * 0.42, y + h * 0.3, x + w * 0.5, sea, x + w * 0.4, sea + 4]);
    poly(c, '#c8ccc4', [x + w * 0.3, sea + 4, x + w * 0.36, y + h * 0.36, x + w * 0.42, y + h * 0.3, x + w * 0.5, sea, x + w * 0.46, sea + 4]);
    rect(c, 'rgba(255,255,255,0.5)', x + w * 0.38, y + h * 0.4, 0.5, h * 0.2);
    // The highway clinging to the cliff.
    line(c, '#f4f0e8', 0.6, [x + 1, y + h * 0.42, x + w * 0.3, y + h * 0.52, x + w * 0.44, y + h * 0.48]);
  } else {
    poly(c, '#4f8a5a', [x, sea, x + w * 0.18, y + h * 0.22, x + w * 0.36, y + h * 0.42, x + w * 0.52, y + h * 0.14, x + w * 0.74, sea]);
    poly(c, '#3a6a4a', [x + w * 0.28, sea, x + w * 0.42, y + h * 0.36, x + w * 0.6, sea]);
    poly(c, '#ffffff', [x + w * 0.52, y + h * 0.14, x + w * 0.47, y + h * 0.24, x + w * 0.57, y + h * 0.24]);
    ellipse(c, '#ffd34e', x + w * 0.84, y + h * 0.24, 2, 2);
  }
}

// ---------------------------------------------------------------------------
// Amis harvest dance
// ---------------------------------------------------------------------------

const BEADS = ['#ffd34e', '#e8473a', '#3aa0d9', '#5fbf5a', '#ffffff', '#ff8ac0'];

function dancer(c: Ctx, x: number, fy: number, i: number, facingIn: number, side: number, step: number, t: number): void {
  const man = i % 2 === 1;
  const lift = Math.max(0, Math.sin(step)) * 1.6;
  const y = fy - lift;
  const skin = i % 3 === 0 ? '#e0a878' : i % 3 === 1 ? '#c98a5e' : '#d89a6a';
  ellipse(c, 'rgba(20,10,6,0.25)', x, fy + 0.4, 3.4, 1.1);
  // Legs: a kick forward on the beat.
  const kick = Math.sin(step) * 1.2;
  rect(c, skin, x - 1.6 + side * kick * 0.4, y - 4, 1.2, 4);
  rect(c, skin, x + 0.4 - side * kick * 0.4, y - 4, 1.2, 4);
  // Ankle bells / leg wraps.
  rect(c, man ? '#e8473a' : '#ffffff', x - 1.8 + side * kick * 0.4, y - 1.6, 1.6, 0.7);
  rect(c, man ? '#e8473a' : '#ffffff', x + 0.2 - side * kick * 0.4, y - 1.6, 1.6, 0.7);
  if (man) {
    // Black loin skirt with a white edge, red vest.
    block(c, x - 2.6, y - 7, 5.2, 3.6, '#1b1d24');
    rect(c, '#ffffff', x - 2.4, y - 3.8, 4.8, 0.5);
    block(c, x - 2.8, y - 12.6, 5.6, 6, '#d6302a', '#f05a4a');
    rect(c, '#1b1d24', x - 0.4, y - 12.4, 0.8, 5.6);
  } else {
    // Red top, black skirt with white and red bands.
    block(c, x - 3, y - 7.6, 6, 4.4, '#1b1d24');
    rect(c, '#ffffff', x - 2.8, y - 4.6, 5.6, 0.6);
    rect(c, '#e8473a', x - 2.8, y - 5.4, 5.6, 0.5);
    block(c, x - 2.6, y - 12.6, 5.2, 5.4, '#e0302a', '#f86050');
    rect(c, '#ffd34e', x - 2.4, y - 8.2, 4.8, 0.5);
  }
  // Bead collar: a bright arc of dots over the shoulders.
  for (let k = 0; k < 7; k++) {
    const a = Math.PI * (0.1 + (k / 6) * 0.8);
    ellipse(c, BEADS[(k + i) % BEADS.length], x + Math.cos(a) * 3, y - 12.4 + Math.sin(a) * 1.6, 0.6, 0.6);
  }
  // Head (face toward the circle's centre when on the far side).
  const hy = y - 15;
  ellipse(c, '#1b1410', x, hy, 2.5, 2.6);
  if (facingIn > 0.15) {
    ellipse(c, skin, x, hy + 0.5, 2.1, 2);
    rect(c, '#1b1410', x - 1, hy + 0.3, 0.5, 0.5);
    rect(c, '#1b1410', x + 0.6, hy + 0.3, 0.5, 0.5);
    rect(c, '#c0504a', x - 0.4, hy + 1.5, 0.9, 0.35);
  } else if (Math.abs(facingIn) <= 0.15) {
    ellipse(c, skin, x + side * 0.9, hy + 0.5, 1.4, 1.9);
  }
  if (man) {
    // Headband with a fan of white and red feathers (a few dancers only).
    rect(c, '#ffd34e', x - 2.6, hy - 1.6, 5.2, 1);
    if (i % 4 === 1) {
      const sway = Math.sin(t * 2 + i) * 0.4;
      for (let f = -2; f <= 2; f++) {
        const fx = x + f * 1.2 + sway, tip = hy - 6.6 + Math.abs(f) * 1.2;
        line(c, f % 2 ? '#e8473a' : '#ffffff', 0.9, [x + f * 0.6, hy - 1.8, fx, tip]);
      }
    }
  } else {
    // Woven headband with pompoms.
    rect(c, '#ffffff', x - 2.7, hy - 1.8, 5.4, 1.2);
    rect(c, '#e8473a', x - 2.7, hy - 1.3, 5.4, 0.4);
    ellipse(c, BEADS[(i + 1) % BEADS.length], x - 2.2, hy - 2.2, 0.9, 0.9);
    ellipse(c, BEADS[(i + 3) % BEADS.length], x + 2.2, hy - 2.2, 0.9, 0.9);
    if (i % 4 === 0) {
      // A tall white feather tuft.
      const sway = Math.sin(t * 2 + i) * 0.5;
      line(c, '#ffffff', 1, [x, hy - 2.4, x + sway, hy - 6.4]);
      ellipse(c, '#ffd34e', x + sway, hy - 6.6, 0.7, 0.7);
    }
  }
}

function amisDance(c: Ctx, r: Rect, t: number): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  const cy = by - 16, rx = 33, ry = 10;
  // A woven mat circle on the square.
  ellipse(c, 'rgba(120,80,40,0.18)', cx, cy + 1, rx + 6, ry + 3);
  ellipse(c, 'rgba(255,240,200,0.12)', cx, cy + 1, rx - 4, ry - 2);
  const N = 10;
  const beat = t * 4.2;
  const rot = t * 0.22;
  const pts = Array.from({ length: N }, (_, i) => {
    const a = rot + (i / N) * Math.PI * 2;
    return { i, a, x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry };
  });
  // Arms held hand in hand behind/along the circle: drawn first so bodies sit over them.
  for (let k = 0; k < N; k++) {
    const p = pts[k], q = pts[(k + 1) % N];
    const swing = Math.sin(beat) * 1.6;
    const mx = (p.x + q.x) / 2, my = (p.y + q.y) / 2 - 11.6 - Math.sin(p.i + q.i + beat) * 0.2 + swing;
    const lift = Math.max(0, Math.sin(beat)) * 1.9;
    line(c, '#c98a5e', 1.3, [p.x, p.y - 13.9 - lift, mx, my, q.x, q.y - 13.9 - lift]);
  }
  // The drum in the middle, beaten on the step.
  const hit = Math.max(0, Math.cos(beat)) ** 6;
  ellipse(c, 'rgba(20,10,6,0.25)', cx, cy + 3, 6, 1.6);
  block(c, cx - 4.5, cy - 6, 9, 8, '#8a4a2a', '#a8603a', '#5a2e18');
  rect(c, '#ffd34e', cx - 4, cy - 3, 8, 0.6);
  rect(c, '#1b1d24', cx - 4, cy - 1.4, 8, 0.5);
  ellipse(c, '#e8d8b0', cx, cy - 6, 4.4, 1.6);
  ellipse(c, `rgba(255,255,255,${0.2 + hit * 0.6})`, cx, cy - 6, 3 + hit * 2, 1 + hit * 0.6);
  line(c, '#5a3a20', 0.8, [cx + 6, cy - 12 + hit * 5, cx + 1.5, cy - 7 + hit * 0.4]);
  ellipse(c, '#fff3d0', cx + 6, cy - 12 + hit * 5, 0.9, 0.9);
  // Dancers, far side first.
  const order = [...pts].sort((a, b) => a.y - b.y);
  for (const p of order) {
    const facingIn = -Math.sin(p.a); // >0 on the far side of the circle (we see faces)
    const side = Math.cos(p.a) > 0 ? -1 : 1;
    c.save();
    c.translate(p.x, p.y);
    c.scale(1.2, 1.2);
    dancer(c, 0, 0, p.i, facingIn, side, beat + (p.i % 2) * 0.15, t);
    c.restore();
  }
  // Music notes drifting up from the drum.
  for (let k = 0; k < 2; k++) {
    const u = (t * 0.35 + k / 2) % 1;
    const nx = cx - 6 + k * 12 + Math.sin(t * 2 + k) * 2, ny = cy - 14 - u * 22;
    c.globalAlpha = 1 - u;
    txt(c, k ? '♫' : '♪', nx, ny, 6, '#ffd34e', 900, 'rgba(0,0,0,0.35)');
    c.globalAlpha = 1;
  }
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

const OBJECTS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  hlQingshui(c, r) {
    // An interpretive panel on two posts showing the cliffs plunging into the sea, plus a stone marker.
    const by = r.y + r.h, cx = r.x + r.w / 2;
    shadow(c, cx, by, 16, 2.4);
    for (const px of [cx - 13, cx + 10.6]) block(c, px, by - 18, 2.4, 18, '#6a4a2a', '#8a6440');
    block(c, cx - 17, by - 36, 34, 22, '#5a3a20', '#7a5030', '#3a2414');
    vista(c, cx - 15.4, by - 34.4, 30.8, 13, true);
    rect(c, '#f4ecd8', cx - 15.4, by - 21.4, 30.8, 6);
    txt(c, '清水斷崖', cx, by - 18.9, 4.6, '#2a4a3a', 900);
    txt(c, 'QINGSHUI CLIFF', cx, by - 16.4, 2, '#55606a', 700);
    // A marble boulder with carved red characters at the foot.
    block(c, cx + 9, by - 8, 11, 8, '#e4e2dc', '#f6f4ee', '#b8b6ae');
    rect(c, 'rgba(120,120,120,0.35)', cx + 11, by - 6, 6, 0.4);
    txt(c, '清水', cx + 14.5, by - 4, 3.4, '#c0392b', 900);
  },
  hlNineTurns(c, r) {
    // A brown national-park trail board with a zig-zag route map.
    const by = r.y + r.h, cx = r.x + r.w / 2;
    shadow(c, cx, by, 15, 2.4);
    for (const px of [cx - 12, cx + 9.6]) block(c, px, by - 20, 2.4, 20, '#5a3a20', '#7a5030');
    block(c, cx - 16, by - 38, 32, 21, '#6a4428', '#8a5a34', '#4a2e18');
    // Little gable roof.
    poly(c, '#3a5a3a', [cx - 19, by - 37, cx + 19, by - 37, cx + 14, by - 42, cx - 14, by - 42]);
    rect(c, '#2a4a2a', cx - 19, by - 37.5, 38, 1);
    txt(c, '九曲洞', cx, by - 33.4, 5.8, '#fff3d0', 900, 'rgba(0,0,0,0.4)');
    txt(c, 'TUNNEL OF NINE TURNS', cx, by - 29.6, 2.1, '#f2dcb0', 700);
    // Route map: the winding road through the marble walls.
    block(c, cx - 13.4, by - 28, 26.8, 9, '#e8e0cc', '#f6f0e0');
    rect(c, '#9ad0d8', cx - 13, by - 21.6, 26, 1.6);
    line(c, '#c0392b', 0.7, [cx - 12, by - 23, cx - 9, by - 26.6, cx - 6, by - 23, cx - 3, by - 26.6, cx, by - 23, cx + 3, by - 26.6, cx + 6, by - 23, cx + 9, by - 26.6, cx + 12, by - 23]);
    ellipse(c, '#ffd34e', cx - 12, by - 23, 0.9, 0.9);
    // Falling-rock warning plate between the posts.
    rect(c, '#5a3a20', cx - 10, by - 12.4, 20, 1.4);
    block(c, cx - 5, by - 15, 10, 7, '#fbf8ef', '#ffffff');
    poly(c, '#ffd34e', [cx - 3.4, by - 9.4, cx + 3.4, by - 9.4, cx, by - 14.2]);
    rect(c, '#1b1d24', cx - 0.3, by - 12.8, 0.6, 1.8);
    rect(c, '#1b1d24', cx - 0.3, by - 10.6, 0.6, 0.6);
  },
  hlPancake: (c, r, t) =>
    stall(c, r, t, '炸彈蔥油餅', '#e2a03a', (c, r, t) => {
      // A big flat griddle with oil shimmer and three puffy golden "bomb" pancakes, one with an egg.
      const gx = r.x + 6, gy = r.y - 13;
      ellipse(c, '#2b2e33', gx + 16, gy + 1, 17, 4.4);
      ellipse(c, '#4a4e54', gx + 16, gy, 15.4, 3.6);
      ellipse(c, 'rgba(255,220,140,0.18)', gx + 12 + Math.sin(t * 3) * 2, gy - 0.6, 6, 1.2);
      for (let i = 0; i < 3; i++) {
        const px = gx + 6 + i * 10, puff = 0.4 * Math.sin(t * 3 + i * 2);
        ellipse(c, '#b8741e', px, gy, 4.6, 2.4 + puff * 0.3);
        ellipse(c, '#e8a83a', px, gy - 0.8 - puff, 4.2, 2.2 + puff * 0.4);
        ellipse(c, '#ffd27a', px - 1, gy - 1.6 - puff, 2.4, 1);
        // Spring onion flecks.
        rect(c, '#3f9a3a', px - 2, gy - 1, 0.7, 0.5);
        rect(c, '#3f9a3a', px + 1.2, gy - 0.4, 0.7, 0.5);
        if (i === 1) {
          ellipse(c, '#fffaf0', px + 0.6, gy - 1.4 - puff, 1.8, 0.9);
          ellipse(c, '#ffb020', px + 0.6, gy - 1.6 - puff, 0.8, 0.5);
        }
        steam(c, px, gy - 4, t + i * 0.37, 2, 0.4);
      }
      // A stack in paper bags ready to go and a sauce bottle.
      for (let k = 0; k < 2; k++) {
        block(c, r.x + r.w - 13 + k * 5, r.y - 17, 4.4, 7, '#f4ead4', '#ffffff');
        rect(c, '#c0392b', r.x + r.w - 12.6 + k * 5, r.y - 14, 3.6, 0.6);
      }
      block(c, r.x + r.w - 4, r.y - 18, 2.6, 8, '#c0392b', '#e05a48');
    }),
  hlWonton: (c, r, t) =>
    stall(c, r, t, '扁食', '#3a8fd9', (c, r, t) => {
      // A big steaming stock pot, a ladle, bowls of wonton soup and a tray of fresh dumplings.
      const px = r.x + 13, py = r.y - 11;
      block(c, px - 10, py - 12, 20, 13, '#b8c0c8', '#dce2e8', '#8a9096');
      rect(c, 'rgba(255,255,255,0.4)', px - 7, py - 10, 1.4, 9);
      ellipse(c, '#d8c8a0', px, py - 12, 9.4, 2.4);
      ellipse(c, '#f0e2c0', px - 1, py - 12.4, 6, 1.4);
      for (let i = 0; i < 4; i++) ellipse(c, '#fbf6ea', px - 5 + i * 3.2, py - 12 + (i % 2) * 0.8, 1.4, 0.8);
      line(c, '#5a3a20', 0.8, [px + 5, py - 12, px + 11, py - 20]);
      steam(c, px - 3, py - 14, t, 3, 0.5);
      steam(c, px + 4, py - 14, t + 0.5, 3, 0.4);
      // Bowls with wontons and green onion.
      for (let k = 0; k < 2; k++) {
        const bx = r.x + 33 + k * 11, byy = r.y - 11;
        c.fillStyle = '#f4f2ec';
        c.beginPath();
        c.ellipse(bx, byy - 2, 4.4, 3.4, 0, 0, Math.PI);
        c.fill();
        rect(c, '#3a8fd9', bx - 4, byy - 0.6, 8, 0.6);
        ellipse(c, '#e8d4a0', bx, byy - 2, 4, 1.2);
        ellipse(c, '#fbf6ea', bx - 1.4, byy - 2.4, 1.3, 0.8);
        ellipse(c, '#fbf6ea', bx + 1.4, byy - 2.2, 1.3, 0.8);
        rect(c, '#3f9a3a', bx - 0.2, byy - 2.8, 0.7, 0.5);
        steam(c, bx, byy - 4, t + k * 0.6 + 0.2, 2, 0.3);
      }
      // A tray of raw wontons with their little tails.
      block(c, r.x + r.w - 12, r.y - 13, 10, 3, '#c8a070', '#e0bc8c');
      for (let i = 0; i < 4; i++) {
        ellipse(c, '#fff6e0', r.x + r.w - 10 + i * 2.2, r.y - 14, 1.1, 0.9);
        rect(c, '#efe0c0', r.x + r.w - 9.6 + i * 2.2, r.y - 15, 0.6, 0.6);
      }
    }),
  hlAmis: amisDance,
  hlWhale(c, r, t) {
    // A little blue ticket booth with a big whale on top.
    const by = r.y + r.h, cx = r.x + r.w / 2;
    shadow(c, cx, by, r.w / 2 + 3, 3);
    block(c, r.x, by - 26, r.w, 26, '#f4f5f2', '#ffffff', '#c8ccd0');
    for (let i = 0; i < 3; i++) rect(c, i % 2 ? '#2f8fd0' : '#ffffff', r.x + 0.6, by - 7 + i * 2, r.w - 1.2, 2);
    // Ticket window with a little awning.
    vgrad(c, r.x + 4, by - 22, r.w - 8, 10, '#3a5a70', '#1c2c38');
    rect(c, 'rgba(180,220,255,0.3)', r.x + 6, by - 21, 3, 8);
    block(c, r.x + 2, by - 12, r.w - 4, 2.4, '#8a6a40', '#a8845a');
    rect(c, '#ffffff', r.x + r.w / 2 - 3, by - 13.6, 6, 1.6);
    poly(c, '#2f8fd0', [r.x - 2, by - 22, r.x + r.w + 2, by - 22, r.x + r.w, by - 26, r.x, by - 26]);
    for (let i = 0; i < r.w; i += 4) rect(c, '#ffffff', r.x + i, by - 25.6, 2, 3.6);
    // Sign board with the whale leaping (bobs gently).
    const bob = Math.sin(t * 1.6) * 0.8;
    block(c, cx - 18, by - 40, 36, 13, '#1f6f9a', '#3a8fc0', '#154a6a');
    rect(c, 'rgba(255,255,255,0.4)', cx - 17, by - 30, 34, 0.5);
    whale(c, cx - 8, by - 33 + bob, 0.85, '#7ac0e8', '#e8f6ff');
    txt(c, '賞鯨', cx + 7, by - 35.4, 6, '#ffffff', 900, 'rgba(0,0,0,0.35)');
    txt(c, 'WHALE WATCHING', cx + 6, by - 30.6, 2.2, '#cfe6f2', 700);
    // Life ring on the side.
    ellipse(c, '#e8473a', r.x + r.w + 0.4, by - 16, 3.4, 3.4);
    ellipse(c, '#ffffff', r.x + r.w + 0.4, by - 16, 3.4, 1);
    ellipse(c, '#f4f5f2', r.x + r.w + 0.4, by - 16, 1.6, 1.6);
    if (isNight()) ellipse(c, 'rgba(160,210,255,0.2)', cx, by - 34, 24, 10);
  },
  htDesk(c, r) {
    const { x, y, w, h } = r;
    const by = y + h, cx = x + w / 2;
    // Key rack and hotel logo on the wall behind.
    block(c, x + 24, y - 40, 22, 14, '#8a5a34', '#a8784a', '#5a3a20');
    for (let row = 0; row < 2; row++)
      for (let i = 0; i < 5; i++) {
        const kx = x + 27 + i * 4, ky = y - 37 + row * 6;
        rect(c, '#d9b450', kx, ky, 0.8, 1.4);
        if ((i + row) % 3 !== 2) {
          rect(c, '#c8ccd0', kx - 0.1, ky + 1.4, 1, 2);
          block(c, kx - 0.8, ky + 3.2, 2.4, 1.8, i % 2 ? '#2f8fa8' : '#c0392b');
        }
      }
    block(c, x + 50, y - 40, 24, 12, '#2f8fa8', '#4aaec8', '#1f6a80');
    whale(c, x + 56, y - 33.6, 0.42, '#ffffff', '#cfeef8');
    txt(c, '海景', x + 66, y - 35.6, 3.6, '#ffffff', 900);
    txt(c, 'HOTEL', x + 66, y - 31.6, 2.4, '#e8f6ff', 700);
    // Counter: wood front with a teal stripe, marble top.
    shadow(c, cx, by, w / 2 + 2, 3, 0.3);
    block(c, x, y - 12, w, h + 12, '#a8784a', '#c8986a', '#7a5030');
    for (let i = x + 6; i < x + w - 3; i += 9) rect(c, 'rgba(0,0,0,0.12)', i, y - 9, 0.6, h + 7);
    rect(c, '#2f8fa8', x + 1, by - 6, w - 2, 1.6);
    block(c, x - 2, y - 15, w + 4, 4, '#f2efe8', '#ffffff', '#c8c4b8');
    rect(c, 'rgba(160,160,170,0.35)', x + 8, y - 13.6, 10, 0.4);
    rect(c, 'rgba(160,160,170,0.35)', x + 30, y - 13, 12, 0.4);
    // Brass plaque.
    block(c, cx - 15, y - 9.6, 30, 6, '#d9b450', '#f0d070', '#a8862a');
    txt(c, '接待處', cx, y - 6.5, 4.4, '#4a3410', 900);
    // On top: monitor (back), guest book and pen, the bell, a little orchid.
    block(c, x + 6, y - 24, 12, 8, '#2b2e33', '#4a4e54');
    rect(c, '#55606a', x + 11, y - 16.4, 2, 2);
    block(c, x + 22, y - 17, 9, 3, '#7a2a24', '#9a3a30');
    rect(c, '#fbf8ef', x + 22.6, y - 17.4, 7.8, 1);
    line(c, '#1b1d24', 0.6, [x + 27, y - 18.4, x + 31, y - 16.6]);
    // Service bell.
    ellipse(c, '#7a6a4a', x + 38, y - 15.4, 3.6, 0.9);
    c.fillStyle = '#e8c048';
    c.beginPath();
    c.ellipse(x + 38, y - 15.6, 3, 3, 0, Math.PI, 0);
    c.fill();
    ellipse(c, '#fff3b0', x + 37, y - 17.4, 0.8, 0.6);
    rect(c, '#a8862a', x + 37.6, y - 19.6, 0.8, 1.2);
    // Orchid in a white pot.
    block(c, x + w - 11, y - 19, 5, 5, '#ffffff', '#ffffff', '#c8ccd0');
    line(c, '#3f7a35', 0.6, [x + w - 8.5, y - 19, x + w - 8, y - 26, x + w - 5, y - 28]);
    for (const [ox, oy] of [[-7.6, -25], [-6, -27.4], [-4, -28.6]]) ellipse(c, '#e890d0', x + w + ox, y + oy, 1.3, 1.1);
    ellipse(c, '#4f9a45', x + w - 10, y - 19.6, 2.4, 0.8);
  },
  htBed(c, r) {
    const { x, y, w, h } = r;
    const by = y + h;
    const top = by - 44;
    shadow(c, x + w / 2, by, w / 2 + 3, 3, 0.3);
    // Headboard (upholstered, teal) against the wall.
    block(c, x - 2, top - 6, w + 4, 14, '#2f8fa8', '#4aaec8', '#1f6a80');
    for (let i = x + 4; i < x + w - 2; i += 8) for (const yy of [top - 2, top + 3]) ellipse(c, 'rgba(0,0,0,0.18)', i, yy, 0.6, 0.6);
    // Frame and mattress.
    block(c, x - 1, top + 6, w + 2, by - top - 6, '#8a5a34', '#a8784a', '#5a3a20');
    block(c, x + 1, top + 6, w - 2, by - top - 10, '#fbfbf8', '#ffffff', '#d8dce0');
    // Two plump pillows.
    for (const px of [x + 3, x + w / 2 + 1]) {
      block(c, px, top + 8, w / 2 - 4, 8, '#ffffff', '#ffffff', '#d8dce0');
      rect(c, 'rgba(160,170,180,0.35)', px + 2, top + 12, w / 2 - 8, 0.5);
    }
    // Duvet with a folded-back top edge and a teal runner across the foot.
    block(c, x + 1, top + 18, w - 2, by - top - 22, '#f4f6f8', '#ffffff', '#c8d0d8');
    rect(c, '#e2e8ee', x + 2, top + 18, w - 4, 3);
    rect(c, 'rgba(255,255,255,0.9)', x + 2, top + 18, w - 4, 0.7);
    for (let i = x + 8; i < x + w - 4; i += 10) rect(c, 'rgba(150,165,180,0.25)', i, top + 23, 0.6, 10);
    block(c, x + 1, by - 13, w - 2, 5, '#2f8fa8', '#4aaec8', '#1f6a80');
    for (let i = x + 3; i < x + w - 2; i += 3) rect(c, 'rgba(255,255,255,0.35)', i, by - 11, 1, 0.6);
    // A folded towel swan, Hualien-hotel style.
    ellipse(c, '#ffffff', x + w / 2, top + 27, 4, 2.2);
    line(c, '#ffffff', 1.2, [x + w / 2 + 2, top + 26.6, x + w / 2 + 3.4, top + 23, x + w / 2 + 2.2, top + 22]);
    ellipse(c, 'rgba(180,190,200,0.6)', x + w / 2, top + 28, 3, 0.8);
    // Bedside table with a warm lamp (left side).
    const nx = x - 15;
    shadow(c, nx + 6, by - 20, 6, 1.6, 0.25);
    block(c, nx, by - 30, 12, 10, '#8a5a34', '#a8784a', '#5a3a20');
    rect(c, '#5a3a20', nx + 2, by - 25, 8, 0.6);
    ellipse(c, '#d9b450', nx + 6, by - 25.4, 0.7, 0.5);
    rect(c, '#d9b450', nx + 5.4, by - 36, 1.2, 6);
    ellipse(c, '#d9b450', nx + 6, by - 30.2, 2.4, 0.8);
    poly(c, '#fff0c8', [nx + 2.4, by - 36, nx + 9.6, by - 36, nx + 8, by - 41, nx + 4, by - 41]);
    rect(c, 'rgba(200,150,80,0.4)', nx + 2.6, by - 36.6, 6.8, 0.6);
    ellipse(c, 'rgba(255,220,150,0.22)', nx + 6, by - 36, 10, 7);
  },
};

// ---------------------------------------------------------------------------
// Decor
// ---------------------------------------------------------------------------

function tarokoGate(c: Ctx, d: Decor): void {
  // The Taroko archway: white pillars with red bases, red beams, a name board and a curved tiled roof.
  const x = d.x, y = d.y, w = d.w ?? 80;
  // Pillars stand at the trail's edges; the roof overhangs the full width.
  const L = Math.round(x - w * 0.33), R = Math.round(x + w * 0.33 - 6);
  for (const px of [L, R]) {
    shadow(c, px + 3, y, 6, 2, 0.3, 40);
    block(c, px - 1, y - 6, 8, 6, '#8a8680', '#a8a49c', '#6a6660');
    block(c, px, y - 50, 6, 46, '#f2efe6', '#ffffff', '#c8c4b8');
    rect(c, 'rgba(0,0,0,0.08)', px + 4, y - 49, 1.4, 44);
    rect(c, '#c0392b', px + 0.6, y - 10, 4.8, 3);
    rect(c, '#c0392b', px + 0.6, y - 48, 4.8, 3);
    rect(c, '#d9b450', px + 0.6, y - 45.6, 4.8, 0.6);
  }
  // Lower beam (kept high so people pass beneath) and the upper lintel.
  block(c, L - 4, y - 46, R - L + 14, 4, '#c0392b', '#e05a48', '#8a2a1e');
  for (let i = L; i < R + 6; i += 6) rect(c, '#2f8a6a', i + 1, y - 45, 3, 1.6);
  block(c, L - 6, y - 56, R - L + 18, 6, '#c0392b', '#e05a48', '#8a2a1e');
  for (let i = L - 4; i < R + 10; i += 5) {
    rect(c, '#2f6fa8', i, y - 55, 2.4, 1.2);
    rect(c, '#2f8a6a', i + 2.4, y - 55, 2.4, 1.2);
  }
  rect(c, '#d9b450', L - 5, y - 52.6, R - L + 16, 0.6);
  // Name board hanging between the beams.
  block(c, x - 14, y - 52, 28, 9, '#1f4a7a', '#2f5f98', '#123058');
  rect(c, '#d9b450', x - 13.4, y - 51.4, 26.8, 0.5);
  rect(c, '#d9b450', x - 13.4, y - 44.6, 26.8, 0.5);
  txt(c, '太魯閣', x, y - 47.4, 6.4, '#ffd34e', 900, 'rgba(0,0,0,0.4)');
  // Tiled roof with upswept eaves.
  const rl = x - w / 2 - 2, rr = x + w / 2 + 2, ry = y - 56;
  c.fillStyle = '#7a2a1a';
  c.beginPath();
  c.moveTo(rl - 2, ry - 7);
  c.quadraticCurveTo(rl + 8, ry + 1, x - w / 4, ry);
  c.lineTo(x + w / 4, ry);
  c.quadraticCurveTo(rr - 8, ry + 1, rr + 2, ry - 7);
  c.lineTo(rr - 6, ry - 6);
  c.lineTo(x + w / 4 - 4, ry - 12);
  c.lineTo(x - w / 4 + 4, ry - 12);
  c.lineTo(rl + 6, ry - 6);
  c.closePath();
  c.fill();
  // Tile courses.
  c.save();
  c.clip();
  vgrad(c, rl - 2, ry - 13, rr - rl + 4, 14, '#e8763a', '#b84a22');
  for (let i = rl - 2; i < rr + 2; i += 2.4) rect(c, 'rgba(90,30,10,0.35)', i, ry - 13, 0.6, 14);
  c.restore();
  rect(c, '#5a1e10', rl + 4, ry - 1, rr - rl - 8, 1);
  // Ridge with curling ends and a central pearl.
  block(c, x - w / 4 + 2, ry - 15, w / 2 - 4, 3.4, '#2f8a6a', '#4aaa8a', '#1f6a4a');
  for (const s of [-1, 1]) {
    const ex = x + s * (w / 4 - 2);
    c.fillStyle = '#2f8a6a';
    c.beginPath();
    c.ellipse(ex + s * 1.2, ry - 16.4, 2, 2.2, 0, 0, Math.PI * 2);
    c.fill();
    ellipse(c, '#ffd34e', ex + s * 1.4, ry - 16.8, 0.7, 0.7);
  }
  ellipse(c, '#ffd34e', x, ry - 16.4, 2, 2);
  ellipse(c, '#fff3b0', x - 0.6, ry - 17, 0.7, 0.7);
}

function hualienSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 26, 2.4, 0.28, 20);
  // Stone base and two timber posts.
  for (const px of [x - 22, x + 19]) {
    block(c, px - 1, y - 4, 5, 4, '#8a8680', '#a8a49c');
    block(c, px, y - 24, 3, 21, '#6a4a2a', '#8a6440');
  }
  // Board with a mountains-meet-the-ocean picture.
  block(c, x - 28, y - 42, 56, 22, '#f4ecd8', '#fffaf0', '#c8b898');
  vista(c, x - 26.6, y - 40.6, 22, 19.2, false);
  // Little sailing whale tail in the sea part of the picture.
  poly(c, '#1f4a6a', [x - 8, y - 26, x - 10.6, y - 29.6, x - 8.4, y - 28, x - 6.4, y - 29.8]);
  txt(c, '花蓮', x + 11.6, y - 35.6, 7.2, '#1f6f9a', 900);
  txt(c, '歡迎您', x + 11.6, y - 28.4, 5.4, '#c0392b', 900);
  txt(c, 'Welcome to Hualien', x + 11.6, y - 23.6, 2.6, '#55606a', 700);
  // Amis-style woven trim along the top.
  for (let i = 0; i < 14; i++) rect(c, ['#e8473a', '#1b1d24', '#ffd34e', '#ffffff'][i % 4], x - 27 + i * 3.86, y - 43, 3.86, 1.4);
  // Flowers planted at the foot.
  for (let i = 0; i < 7; i++) {
    const fx = x - 18 + i * 6;
    ellipse(c, '#3f7a35', fx, y - 1.4, 2.4, 1.2);
    ellipse(c, ['#ff8ac0', '#ffd34e', '#ffffff', '#e8473a'][i % 4], fx + 0.4, y - 2.6, 1, 1);
  }
}

function pier(c: Ctx, d: Decor, t: number): void {
  // Wooden jetty on posts reaching east over the sea, with a white boat moored alongside.
  const x0 = d.x, y = d.y, w = d.w ?? 96, x1 = x0 + w;
  const deckTop = y - 10, deckBot = y;
  // Posts down into the water, with ripples.
  for (let px = x0 + 4; px <= x1 - 2; px += 16) {
    for (const py of [deckTop + 1, deckBot + 4]) {
      const k = (t * 0.7 + px * 0.05) % 1;
      ellipse(c, `rgba(255,255,255,${0.5 * (1 - k)})`, px + 1.4, py + 1.2, 2.2 + k * 3, 0.8 + k);
      rect(c, '#4a3020', px, py - 3, 2.8, 4.2);
      rect(c, 'rgba(20,60,80,0.35)', px, py, 2.8, 1.2);
    }
  }
  // Deck.
  shadow(c, (x0 + x1) / 2, deckBot + 3, w / 2, 2, 0.25);
  block(c, x0, deckTop, w, deckBot - deckTop, '#a8784a', '#c8986a', '#7a5030');
  for (let i = x0 + 3; i < x1 - 1; i += 3) rect(c, 'rgba(70,40,20,0.3)', i, deckTop + 0.8, 0.5, deckBot - deckTop - 1.6);
  rect(c, '#6a4428', x0, deckBot - 1.4, w, 1.4);
  // Low rail on the far side and a bollard with the mooring line.
  rect(c, '#6a4a2a', x0, deckTop - 5, w, 1.2);
  for (let i = x0; i <= x1 - 1; i += 12) rect(c, '#5a3a20', i, deckTop - 5, 1.4, 5);
  const bx = x1 - 22;
  block(c, bx, deckBot - 6, 3.6, 4, '#3a3e44', '#5a5e64');
  // The boat bobbing on the south side.
  const bob = Math.sin(t * 1.4) * 0.9, tilt = Math.sin(t * 1.1) * 0.03;
  const hx = x0 + w * 0.45, hy = deckBot + 13 + bob;
  line(c, '#e8d8b0', 0.5, [bx + 1.8, deckBot - 4, hx + 18, hy - 6]);
  c.save();
  c.translate(hx, hy);
  c.rotate(tilt);
  ellipse(c, 'rgba(20,60,80,0.35)', 2, 3.4, 26, 3);
  // Hull.
  c.fillStyle = '#e8ecef';
  c.beginPath();
  c.moveTo(-24, -6);
  c.lineTo(26, -6);
  c.quadraticCurveTo(24, 1, 16, 2.4);
  c.lineTo(-20, 2.4);
  c.quadraticCurveTo(-24, 0, -24, -6);
  c.fill();
  rect(c, '#2f6fa8', -23, -2, 46, 1.4);
  rect(c, '#c0392b', -20, 1.2, 36, 1.2);
  rect(c, '#ffffff', -23.6, -6.6, 50, 1);
  // Cabin with windows and a little flag.
  block(c, -10, -15, 20, 9, '#ffffff', '#ffffff', '#c8ccd0');
  for (let i = 0; i < 3; i++) rect(c, '#3a5a70', -7.6 + i * 6, -12.6, 4, 3);
  block(c, -8, -17, 16, 2.4, '#2f6fa8', '#4a8ac8');
  line(c, '#55606a', 0.5, [8, -17, 8, -24]);
  const fl = Math.sin(t * 4) * 0.8;
  poly(c, '#e8473a', [8, -24, 13, -23 + fl, 8, -21.4]);
  // Life ring and a whale decal on the hull.
  ellipse(c, '#e8473a', 18, -10, 2.2, 2.2);
  ellipse(c, '#ffffff', 18, -10, 1, 1);
  c.restore();
  whale(c, hx - 16, hy - 3.2 + 0, 0.38, '#2f6fa8', '#cfe6f2');
  // Lamp post at the far end (glows after dark).
  const lx = x1 - 4;
  rect(c, '#3a3e44', lx, deckTop - 22, 1.6, 22);
  block(c, lx - 1.6, deckTop - 26, 4.8, 4.4, '#3a3e44');
  rect(c, isNight() ? '#fff3b0' : '#e8e0c8', lx - 0.8, deckTop - 25, 3.2, 2.6);
  if (isNight()) {
    ellipse(c, 'rgba(255,230,150,0.25)', lx + 0.8, deckTop - 23.6, 10, 8);
    ellipse(c, 'rgba(255,230,150,0.18)', lx + 0.8, deckBot + 10, 8, 3);
  }
  // Sparkles on the water.
  for (let i = 0; i < 4; i++) {
    const k = (t * 0.5 + i / 4) % 1;
    rect(c, `rgba(255,255,255,${0.7 * Math.sin(k * Math.PI)})`, x0 + 10 + i * 22, deckBot + 22 + (i % 2) * 4, 2, 0.6);
  }
}

registerPropDrawers({
  objects: OBJECTS,
  decor: (c, d, t) => {
    switch (d.kind) {
      case 'tarokoGate':
        tarokoGate(c, d);
        return true;
      case 'hualienSign':
        hualienSign(c, d);
        return true;
      case 'pier':
        pier(c, d, t);
        return true;
      default:
        return false;
    }
  },
  animatedObjects: ['hlPancake', 'hlWonton', 'hlAmis', 'hlWhale'],
  animatedDecor: ['pier'],
});
